import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { getConfig } from "../config";
import { subMonths, subYears } from "date-fns";
import { getAttachments, getDashboard, getSnapshots, getSnapshotGroups, getTransactions } from "../api/portfolio";
import { getExchangeRates, getCryptoPrices } from "../api/market";
import { isTransient } from "../queryClient";

// The history queries retry for longer than the default: a deploy restarts
// the single API machine and it takes ~10 s to come back, and the chart
// has nothing cached to fall back on. Six tries span about 20 s.
const HISTORY_RETRY = {
  retry: (failureCount, error) => failureCount < 6 && isTransient(error),
  retryDelay: (attempt) => Math.min(800 * 2 ** attempt, 5000),
};

const RATES_CACHE_KEY = "exchangeRates";
const RATES_TS_KEY = "exchangeRatesTimestamp";
const RATES_STALE_MS = 30 * 60 * 1000; // 30 minutes

/** Assets, liabilities, totals and net worth — the dashboard payload. */
export function useDashboardData() {
  return useQuery({
    queryKey: ["dashboard"],
    queryFn: getDashboard,
    // The server answers with cached prices while it refreshes stale ones
    // in the background; poll until it reports the refresh is done.
    refetchInterval: (query) => (query.state.data?.prices_refreshing ? 4000 : false),
  });
}

function rangeStartDate(dateRange) {
  const now = new Date();
  if (dateRange === "1m") return subMonths(now, 1).toISOString().split("T")[0];
  if (dateRange === "3m") return subMonths(now, 3).toISOString().split("T")[0];
  if (dateRange === "6m") return subMonths(now, 6).toISOString().split("T")[0];
  if (dateRange === "1y") return subYears(now, 1).toISOString().split("T")[0];
  return null; // "all"
}

/** Net-worth history for the chart; keeps the old range's data visible
 *  while a new range loads (spinner overlays instead of blanking). */
export function useSnapshots(dateRange) {
  return useQuery({
    queryKey: ["snapshots", dateRange],
    queryFn: () => getSnapshots(rangeStartDate(dateRange)),
    placeholderData: keepPreviousData,
    ...HISTORY_RETRY,
  });
}

/** Per-group history for the Assets page charts; same range semantics as
 *  useSnapshots. Invalidated with ["snapshots"] since it reads the same rows. */
export function useSnapshotGroups(dateRange) {
  return useQuery({
    queryKey: ["snapshots", "groups", dateRange],
    queryFn: () => getSnapshotGroups(rangeStartDate(dateRange)),
    placeholderData: keepPreviousData,
    ...HISTORY_RETRY,
  });
}

/** ₹-per-currency FX map, mirrored to localStorage so a fresh page load
 *  (or a dead FX API) starts from the last known rates instantly. */
export function useExchangeRates() {
  let initialData;
  let initialDataUpdatedAt;
  try {
    const cached = getConfig().storage.getItem(RATES_CACHE_KEY);
    const ts = parseInt(getConfig().storage.getItem(RATES_TS_KEY), 10);
    if (cached && ts) {
      initialData = JSON.parse(cached);
      initialDataUpdatedAt = ts;
    }
  } catch {
    /* corrupt cache — fetch fresh */
  }

  return useQuery({
    queryKey: ["exchange-rates"],
    queryFn: async () => {
      const data = await getExchangeRates();
      const rates = data?.rates;
      if (!rates) throw new Error("no rates in response");
      getConfig().storage.setItem(RATES_CACHE_KEY, JSON.stringify(rates));
      getConfig().storage.setItem(RATES_TS_KEY, Date.now().toString());
      return rates;
    },
    staleTime: RATES_STALE_MS,
    initialData,
    initialDataUpdatedAt,
    // On failure fall back to whatever we had; getUsdRate() has a
    // hardcoded last-resort rate for the truly-empty case.
    placeholderData: { USD: 83.0, EUR: 90.0 },
  });
}

/** A ledger portfolio's entries, newest first. */
export function useTransactions(assetId) {
  return useQuery({
    queryKey: ["transactions", assetId],
    queryFn: () => getTransactions(assetId),
    enabled: Boolean(assetId),
  });
}

/** Proof files attached to one entry. */
export function useAttachments(assetId, txId) {
  return useQuery({
    queryKey: ["attachments", txId],
    queryFn: () => getAttachments(assetId, txId),
    enabled: Boolean(assetId && txId),
  });
}

/** Cached market prices for the crypto editor (backend refreshes hourly). */
export function useCryptoPrices() {
  return useQuery({
    queryKey: ["crypto-prices"],
    queryFn: getCryptoPrices,
    staleTime: 15 * 60 * 1000,
    placeholderData: [],
  });
}
